import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { TaskExecutor } from '../../types';
import type { ChangeEvent, FC, UIEvent } from 'react';

import './ResponsibleDropdown.scss';

interface ResponsibleDropdownProps {
  executors: readonly TaskExecutor[];
  selectedId: TaskExecutor['id'] | null;
  onSelect(executor: TaskExecutor): void;
  onClose(): void;
}

// ─── Virtual scroll constants ──────────────────────────────────────────────
const VIEWPORT_HEIGHT = 240;
const OVERSCAN = 3;
const LINE_HEIGHT = 18;
const ITEM_V_PADDING = 14; // 7px top + 7px bottom
// 200px width − 10px*2 (left/right padding) − 20px reserve for checkmark & gap
const EFFECTIVE_TEXT_WIDTH = 160;

// Build cumulative top offsets for all items. offsets[n] = total height.
const buildOffsets = (items: readonly TaskExecutor[]): number[] => {
  // Canvas for pixel-accurate text measurement; falls back to char estimation
  let measure: (text: string) => number;
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font =
        '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      measure = (text) => ctx.measureText(text).width;
    } else {
      measure = (text) => text.length * 6.5;
    }
  } catch {
    measure = (text) => text.length * 6.5;
  }

  const offsets: number[] = [];
  let acc = 0;
  for (const item of items) {
    offsets.push(acc);
    const lineCount = Math.min(
      Math.max(Math.ceil(measure(item.name) / EFFECTIVE_TEXT_WIDTH), 1),
      4
    );
    acc += ITEM_V_PADDING + lineCount * LINE_HEIGHT;
  }
  offsets.push(acc); // sentinel = total height
  return offsets;
};

export const ResponsibleDropdown: FC<ResponsibleDropdownProps> = ({
  executors,
  selectedId,
  onSelect,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return executors;
    return executors.filter((e) => e.name.toLowerCase().includes(q));
  }, [executors, search]);

  const offsets = useMemo(() => buildOffsets(filtered), [filtered]);
  const totalHeight = offsets[offsets.length - 1] ?? 0;

  const visibleRange = useMemo((): [number, number] => {
    const n = filtered.length;
    if (n === 0) return [0, -1];

    let start = 0;
    while (start < n && offsets[start + 1]! <= scrollTop) start++;

    let end = start;
    while (end < n - 1 && offsets[end + 1]! < scrollTop + VIEWPORT_HEIGHT)
      end++;

    return [Math.max(0, start - OVERSCAN), Math.min(n - 1, end + OVERSCAN)];
  }, [scrollTop, offsets, filtered.length]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, []);

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const handleSelect = useCallback(
    (executor: TaskExecutor) => {
      onSelect(executor);
      onClose();
    },
    [onSelect, onClose]
  );

  const [start, end] = visibleRange;

  return (
    <div className="ResponsibleDropdown">
      <div className="ResponsibleDropdown__search">
        <Input
          autoFocus
          allowClear
          prefix={
            <SearchOutlined className="ResponsibleDropdown__search-icon" />
          }
          placeholder="Поиск..."
          size="small"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div
        ref={listRef}
        className="ResponsibleDropdown__list"
        onScroll={handleScroll}
      >
        {filtered.length > 0 ? (
          <div style={{ height: totalHeight, position: 'relative' }}>
            {Array.from({ length: end - start + 1 }, (_, i) => {
              const idx = start + i;
              const executor = filtered[idx]!;
              const top = offsets[idx]!;
              const height = offsets[idx + 1]! - top;
              const isSelected = executor.id === selectedId;

              return (
                <div
                  key={executor.id}
                  className={[
                    'ResponsibleDropdown__item',
                    isSelected && 'ResponsibleDropdown__item--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ position: 'absolute', top, height, width: '100%' }}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(executor)}
                >
                  <span className="ResponsibleDropdown__item-name">
                    {executor.name}
                  </span>
                  {isSelected && (
                    <CheckOutlined className="ResponsibleDropdown__item-check" />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ResponsibleDropdown__empty">Не найдено</div>
        )}
      </div>
    </div>
  );
};
